import i18n from "i18next";
import { useState, useMemo, useCallback } from "react";
import { Button } from "@/components/lilia-ui/button";
import { IconCopy } from "@tabler/icons-react";
import type { Block } from "@/lib/api";
import { lmlLanguage } from "@/lib/codemirror/lml-language";
import { formatDocumentDateDisplay, toLatexDateValue } from "@/lib/document-date";
import { SourceCodeViewer } from "./SourceCodeViewer";
import { buildBlockLineMap, lineToBlockId, allMappedLines, type LineRange } from "./block-line-map";
import styles from "./LaTeXPreview.module.scss";

interface BibEntry {
  id: string;
  citeKey: string;
  entryType: string;
  fields: Record<string, string>;
}

interface LMLPreviewProps {
  blocks: Block[];
  bibliographyEntries?: BibEntry[];
  focusedBlockId?: string | null;
  onScrollToBlock?: (blockId: string) => void;
}

function convertToLml(text: string): string {
  if (!text) return text;
  let result = text;
  result = result.replace(/\*\*([^*]+)\*\*/g, "@b{$1}");
  result = result.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "@i{$1}");
  result = result.replace(/__([^_]+)__/g, "@u{$1}");
  result = result.replace(/~~([^~]+)~~/g, "@s{$1}");
  result = result.replace(/`([^`]+)`/g, "@code{$1}");
  result = result.replace(/\$([^$]+)\$/g, "@m{$1}");
  return result;
}

function blockToLml(block: Block, bibliographyEntries?: BibEntry[]): string {
  const content = block.content || {};
  switch (block.type) {
    case "heading": {
      const text = (content.text as string) || "";
      const level = (content.level as number) || 1;
      return `@heading[level=${level}]\n  ${text || "(empty)"}`;
    }
    case "paragraph": {
      const text = (content.text as string) || "";
      return `@paragraph\n  ${convertToLml(text) || "(empty)"}`;
    }
    case "equation": {
      const latex = (content.latex as string) || "";
      const mode = (content.equationMode as string) || "display";
      const label = (content.label as string) || "";
      const attrParts = ["mode=" + mode];
      if (label) attrParts.push('label="' + label + '"');
      return "@equation[" + attrParts.join(", ") + "]\n  " + (latex || "(empty)");
    }
    case "code": {
      const code = (content.code as string) || "";
      const language = (content.language as string) || "plaintext";
      return `@code[lang=${language}]\n  ${code || "(empty)"}`;
    }
    case "table": {
      const headers = (content.headers as string[]) || [];
      const rows = (content.rows as string[][]) || [];
      const headerLine = `| ${headers.join(" | ")} |`;
      const separator = `| ${headers.map(() => "---").join(" | ")} |`;
      const rowLines = rows.map((row) => `| ${row.join(" | ")} |`).join("\n");
      return `@table\n${headerLine}\n${separator}\n${rowLines}`;
    }
    case "figure": {
      const src = (content.src as string) || "";
      const caption = (content.caption as string) || "";
      const alt = (content.alt as string) || "";
      return '@figure[src="' + (src || "(none)") + '", alt="' + alt + '"]\n  ' + (caption || "(no caption)");
    }
    case "theorem": {
      const theoremType = (content.theoremType as string) || "theorem";
      const title = (content.title as string) || "";
      const text = (content.text as string) || "";
      const label = (content.label as string) || "";
      const attrParts = [theoremType];
      if (title) attrParts.push('title="' + title + '"');
      if (label) attrParts.push('label="' + label + '"');
      return "@theorem[" + attrParts.join(", ") + "]\n  " + (text || "(empty)");
    }
    case "abstract": {
      const text = (content.text as string) || "";
      return `@abstract\n  ${text || "(empty)"}`;
    }
    case "title": {
      // Structured title page — display resolves \today; latex attr is raw token.
      const t = (content.title as string) || "";
      const a = (content.author as string) || "";
      const dDisp = formatDocumentDateDisplay(
        content.date as string,
        content.dateMode as string,
      );
      const dLatex = toLatexDateValue(
        content.date as string,
        content.dateMode as string,
      );
      return (
        `@title\n  ${t || "(untitled)"}\n` +
        `@author\n  ${a || "(no author)"}\n` +
        `@date[latex="${dLatex}"]\n  ${dDisp}`
      );
    }
    case "bibliography": {
      if (!bibliographyEntries || bibliographyEntries.length === 0)
        return "@bibliography\n  (no entries)";
      const entries = bibliographyEntries
        .map(
          (entry) =>
            `  @cite[${entry.citeKey}] ${entry.fields.author || "Unknown"} - ${entry.fields.title || "Untitled"}`
        )
        .join("\n");
      return `@bibliography\n${entries}`;
    }
    case "tableOfContents":
      return '@toc[title="' + ((content.title as string) || "Table of Contents") + '"]';
    case "pageBreak":
      return "@pagebreak";
    case "columnBreak":
      return "@columnbreak";
    case "list": {
      const items = (content.items as string[]) || [];
      const ordered = (content.ordered as boolean) || false;
      const listType = ordered ? "ordered" : "unordered";
      const itemLines =
        items.length > 0
          ? items.map((item) => `  - ${item}`).join("\n")
          : "  - (empty)";
      return `@list[type=${listType}]\n${itemLines}`;
    }
    default:
      return `@${block.type}\n  ${JSON.stringify(content)}`;
  }
}

export function LMLPreview({ blocks, bibliographyEntries, focusedBlockId, onScrollToBlock }: LMLPreviewProps) {
  const [copied, setCopied] = useState(false);

  const { lml, lineMap } = useMemo(() => {
    const outputs = blocks.map((block) => ({
      blockId: block.id,
      text: blockToLml(block, bibliographyEntries),
    }));
    return {
      lml: outputs.map((o) => o.text).join("\n\n"),
      lineMap: buildBlockLineMap(outputs),
    };
  }, [blocks, bibliographyEntries]);

  const clickableLines = useMemo(() => allMappedLines(lineMap), [lineMap]);

  const highlightRange = useMemo<{ start: number; end: number } | undefined>(() => {
    if (!focusedBlockId) return undefined;
    return lineMap.get(focusedBlockId) ?? undefined;
  }, [focusedBlockId, lineMap]);

  const handleLineClick = useCallback((lineNum: number) => {
    if (!onScrollToBlock) return;
    const blockId = lineToBlockId(lineNum, lineMap);
    if (blockId) onScrollToBlock(blockId);
  }, [lineMap, onScrollToBlock]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(lml);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <Button
          variant="subtle"
          size="sm"
          onClick={handleCopy}
          title={i18n.t("editor:lmlPreview.copyToClipboard")}
        >
          <IconCopy size={16} /> {copied ? "Copied!" : "Copy"}
        </Button>
      </div>
      <SourceCodeViewer
        value={lml || "(empty document)"}
        language={lmlLanguage}
        highlightRange={highlightRange}
        clickableLines={clickableLines}
        onLineClick={handleLineClick}
      />
    </div>
  );
}
